import { useQuery } from 'react-query';

import { getV2ApiCompanyIdInternationalTransfersRecipientsId } from 'src/client/spendesk-api';

class BadGatewayError extends Error {
  constructor(message: string) {
    super(message);
  }
}

export function useGetInternationalRecipientByIdQuery({
  id,
  companyId,
  enabled,
}: {
  id: string | undefined;
  companyId: string;
  enabled: boolean;
}) {
  const {
    data: internationalRecipient,
    isLoading,
    refetch,
    remove,
  } = useQuery(
    ['international-recipients', id],
    async () => {
      if (!id) {
        throw new Error('Client recipient id is undefined.');
      }

      const result = await getV2ApiCompanyIdInternationalTransfersRecipientsId({
        path: {
          companyId,
          id,
        },
      });

      if (result.statusCode === 502) {
        throw new BadGatewayError(result.body.reason);
      }

      if (result.statusCode !== 200) {
        throw new Error('Failed to fetch recipient by client reference.');
      }

      return result.body.data;
    },
    {
      enabled,
      retry: (failureCount, error) => {
        if (failureCount > 1) {
          return false;
        }

        return !(error instanceof BadGatewayError);
      },
      refetchOnMount: false,
    },
  );

  // Build the international recipient data for the Table data display.
  let internationalRecipientDetails: Array<{
    id: string;
    label: string;
    value: string;
  }> = [];

  if (internationalRecipient) {
    // Add hardcoded name and country fields.
    internationalRecipientDetails.push({
      id: 'accountHolderName',
      label: 'Account Name',
      value: internationalRecipient.accountHolderName,
    });
    internationalRecipientDetails.push({
      id: 'country',
      label: 'Country',
      value: internationalRecipient.country,
    });

    // Concat the dynamic fields coming from the details array.
    internationalRecipientDetails = internationalRecipientDetails.concat(
      internationalRecipient.details?.map((it) => ({
        id: it.key,
        label: it.label,
        value: it.value,
      })) ?? [],
    );
  }

  return {
    internationalRecipientDetails,
    internationalRecipient,
    isLoading,
    refetch,
    remove,
  };
}
